import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, Building2, Clock, Download, Loader2, QrCode,
  ShieldAlert, ShieldQuestion, UserCheck,
} from "lucide-react";

/**
 * Le visite degli ospiti, come le legge l'azienda.
 *
 * Una riga per visita: chi e' passato, da che azienda, quanti QR ha aperto e
 * cosa ha rilevato. I rilievi sono la parte che conta - per questo le visite
 * che ne hanno stanno in cima e si vedono anche senza aprire la scheda.
 */

const ESITO = {
  verde:  { testo: "in regola",   classe: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  giallo: { testo: "in scadenza", classe: "bg-amber-100 text-amber-900 border-amber-200" },
  rosso:  { testo: "fuori regola", classe: "bg-red-100 text-red-800 border-red-200" },
};

const GRAVITA_COLORE = {
  osservazione: "bg-sky-100 text-sky-800 border-sky-200",
  difformita: "bg-amber-100 text-amber-900 border-amber-200",
  grave: "bg-red-100 text-red-800 border-red-200",
};

function dataOraIt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit",
                                     year: "numeric", hour: "2-digit",
                                     minute: "2-digit" });
}

function oraIt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "" : d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

async function scaricaRapporto(id) {
  try {
    const { data, headers } = await api.get(`/visite/${id}/pdf`, { responseType: "blob" });
    const proposto = (headers["content-disposition"] || "").split("filename=")[1];
    const nome = proposto ? proposto.split('"').join("").trim() : "visita.pdf";
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url; a.download = nome;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Errore nella generazione del rapporto");
  }
}

// -----------------------------------------------------------------------------
export default function VisiteEsterne() {
  const [visite, setVisite] = useState([]);
  const [carico, setCarico] = useState(true);
  const [filtro, setFiltro] = useState("tutte");
  const [aperta, setAperta] = useState(null);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = new URLSearchParams();
      if (filtro === "aperte") p.set("aperte", "true");
      if (filtro === "rilievi") p.set("con_difformita", "true");
      const { data } = await api.get(`/visite?${p.toString()}`);
      setVisite(data);
    } catch {
      toast.error("Errore nel caricamento delle visite");
    } finally { setCarico(false); }
  }, [filtro]);

  useEffect(() => { carica(); }, [carica]);

  if (aperta) {
    return <Dettaglio id={aperta} onIndietro={() => { setAperta(null); carica(); }} />;
  }

  const conRilievi = visite.filter(
    (v) => v.quante_difformita > 0 || v.quanti_alert > 0);

  return (
    <div className="space-y-5" data-testid="visite-esterne">
      <div>
        <h2 className="font-display text-xl font-black tracking-tight">Visite esterne</h2>
        <p className="text-sm text-slate-500">
          Chi visita un cantiere — coordinatori, tecnici del committente, ispettori —
          entra con un codice del preposto e dichiara chi è. Qui c&apos;è il giro che ha
          fatto, cosa gli si è presentato davanti e cosa ha segnalato.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[["tutte", "Tutte"], ["aperte", "In corso adesso"],
          ["rilievi", "Con qualcosa da guardare"]].map(([id, nome]) => (
          <button key={id} type="button" onClick={() => setFiltro(id)}
                  data-testid={`filtro-visite-${id}`}
                  className={`px-4 h-10 rounded-full text-sm font-medium border transition-colors ${
                    filtro === id ? "bg-[#0067b2] text-white border-[#0067b2]"
                                  : "bg-white text-slate-600 border-slate-200"}`}>
            {nome}
          </button>
        ))}
      </div>

      {carico && (
        <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
        </p>
      )}

      {!carico && conRilievi.length > 0 && filtro === "tutte" && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          {conRilievi.length} {conRilievi.length === 1 ? "visita" : "visite"} con
          qualcosa da guardare: rilievi del visitatore o schede trovate fuori regola.
        </div>
      )}

      {!carico && visite.length === 0 && (
        <p className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-200 rounded-lg">
          Nessuna visita registrata.
        </p>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {visite.map((v) => (
          <Card key={v.id} className="border-slate-200 hover:border-slate-300 transition-colors">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold leading-tight truncate">{v.nome}</h3>
                  <p className="text-xs text-slate-500 truncate">{v.ruolo}</p>
                  <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                    <Building2 className="w-3 h-3 shrink-0" /> {v.azienda}
                  </p>
                </div>
                {v.aperta ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 shrink-0">
                    in corso
                  </Badge>
                ) : (
                  <span className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">
                    {dataOraIt(v.aperta_il)}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="inline-flex items-center gap-1 bg-slate-100 rounded px-2 py-1">
                  <QrCode className="w-3 h-3" /> {v.quanti_qr} QR
                  {v.qr_distinti !== v.quanti_qr && (
                    <span className="text-slate-400">({v.qr_distinti} punti)</span>
                  )}
                </span>
                {v.durata_minuti !== null && (
                  <span className="inline-flex items-center gap-1 bg-slate-100 rounded px-2 py-1">
                    <Clock className="w-3 h-3" /> {v.durata_minuti} min
                  </span>
                )}
                {v.quante_difformita > 0 && (
                  <span className={`inline-flex items-center gap-1 border rounded px-2 py-1 ${
                    v.difformita_gravi ? GRAVITA_COLORE.grave : GRAVITA_COLORE.difformita}`}>
                    <AlertTriangle className="w-3 h-3" /> {v.quante_difformita} segnalati
                  </span>
                )}
                {v.quanti_alert > 0 && (
                  <span className={`inline-flex items-center gap-1 border rounded px-2 py-1 ${ESITO.rosso.classe}`}>
                    <ShieldAlert className="w-3 h-3" /> {v.alert_rossi} schede fuori regola
                  </span>
                )}
              </div>

              {v.cantiere_nome && (
                <p className="text-xs text-slate-500">Cantiere: {v.cantiere_nome}</p>
              )}
              {!v.aperta && v.motivo_chiusura === "inattivita" && (
                <p className="text-xs text-slate-400">{v.motivo_chiusura_nome}</p>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setAperta(v.id)}
                        data-testid={`apri-visita-${v.id}`} className="h-10 flex-1">
                  Vedi il giro
                </Button>
                <Button variant="outline" onClick={() => scaricaRapporto(v.id)}
                        className="h-10">
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function Dettaglio({ id, onIndietro }) {
  const [v, setV] = useState(null);
  const [carico, setCarico] = useState(true);

  useEffect(() => {
    let vivo = true;
    api.get(`/visite/${id}`)
      .then(({ data }) => { if (vivo) setV(data); })
      .catch(() => toast.error("Visita non trovata"))
      .finally(() => { if (vivo) setCarico(false); });
    return () => { vivo = false; };
  }, [id]);

  if (carico || !v) {
    return (
      <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
      </p>
    );
  }

  return (
    <div className="space-y-5" data-testid="dettaglio-visita">
      <div>
        <button type="button" onClick={onIndietro}
                className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-1">
          <ArrowLeft className="w-3 h-3" /> Tutte le visite
        </button>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-mono text-slate-400">{v.id}</div>
            <h2 className="font-display text-2xl font-black tracking-tight leading-tight">
              {v.nome}
            </h2>
            <p className="text-sm text-slate-500">{v.ruolo} · {v.azienda}</p>
          </div>
          <Button onClick={() => scaricaRapporto(v.id)}
                  className="bg-[#0067b2] hover:bg-[#00589b] text-white h-11">
            <Download className="w-4 h-4 mr-2" /> Rapporto in PDF
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 grid gap-3 sm:grid-cols-2 text-sm">
          {[["Aperta il", dataOraIt(v.aperta_il)],
            ["Chiusa il", dataOraIt(v.chiusa_il) || "ancora aperta"],
            ["Come si è chiusa", v.motivo_chiusura_nome || "—"],
            ["Durata", v.durata_minuti !== null ? `${v.durata_minuti} minuti` : "—"],
            ["Cantiere", v.cantiere_nome || "non indicato"],
            ["Motivo", v.motivo || "non indicato"],
            ["Recapiti", [v.email, v.telefono].filter(Boolean).join(" · ") || "non lasciati"],
            ["QR aperti", `${v.quanti_qr} (${v.qr_distinti} punti diversi)`],
            ["Trovato fuori regola", v.quanti_alert
              ? `${v.alert_rossi} schede su ${v.quanti_qr}, ${v.quanti_alert} rilievi`
              : "niente"]].map(([a, b]) => (
            <div key={a}>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{a}</div>
              <div>{b}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-2">
          <h3 className="font-semibold flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-[#0067b2]" /> Il giro
          </h3>
          <p className="text-xs text-slate-500">
            L&apos;esito lo calcola il gestionale sui dati mostrati al visitatore nel
            momento in cui ha aperto la scheda. Non è una segnalazione sua: è quello
            che gli si è presentato davanti.
          </p>
          {v.tappe.length === 0 && (
            <p className="text-sm text-slate-400">Nessun QR aperto durante la visita.</p>
          )}
          {v.tappe.map((t, i) => {
            const e = ESITO[t.esito] || ESITO.verde;
            return (
              <div key={i} className="py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 tabular-nums w-11 shrink-0">
                    {oraIt(t.quando)}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 shrink-0">
                    {t.tipo_nome || t.tipo}
                  </span>
                  <span className="text-sm truncate flex-1">{t.nome}</span>
                  <span className={`text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 shrink-0 ${e.classe}`}>
                    {e.testo}
                  </span>
                </div>
                {(t.rilievi || []).length > 0 && (
                  <div className="mt-1.5 ml-14 space-y-0.5">
                    {t.rilievi.map((r, j) => (
                      <div key={j} className="text-xs flex items-start gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                          r.gravita === "rosso" ? "bg-red-500" : "bg-amber-500"}`} />
                        <span>
                          <span className="font-medium">{r.cosa}</span>
                          <span className="text-slate-500"> — {r.dettaglio}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className={v.difformita.length ? "border-amber-300" : undefined}>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold flex items-center gap-2">
            <ShieldQuestion className="w-4 h-4 text-[#0067b2]" /> Difformità rilevate
          </h3>
          {v.difformita.length === 0 && (
            <p className="text-sm text-slate-400">Nessuna difformità segnalata.</p>
          )}
          {v.difformita.map((d) => (
            <div key={d.id} className="border border-slate-200 rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 ${
                  GRAVITA_COLORE[d.gravita] || GRAVITA_COLORE.difformita}`}>
                  {d.gravita_nome}
                </span>
                <span className="text-xs text-slate-400">{oraIt(d.quando)}</span>
                {d.dove && <span className="text-xs text-slate-600">· {d.dove}</span>}
              </div>
              <p className="text-sm whitespace-pre-wrap">{d.descrizione}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
